import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, deleteDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AddIcon from '@mui/icons-material/Add';
import PlaybookCard from './PlaybookCard';
import { getAuth } from 'firebase/auth';
import { formatExpectedReturn, toneOfSigned } from '../utils/returns';
import { savedPick } from '../utils/builder';

const ODIKAI_API_BASE = process.env.REACT_APP_ODIKAI_API_BASE || '/api/odikai';

export default function Playbooks() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [refreshingPlaybook, setRefreshingPlaybook] = useState(null);

  useEffect(() => {
    async function fetchPlaybooks() {
      if (!user) return;
      
      setLoading(true);
      try {
        const playbooksRef = collection(db, 'playbooks');
        const q = query(
          playbooksRef, 
          where('is_prebuilt', '==', false),
          where('user_id', '==', user.uid)
        );
        const querySnapshot = await getDocs(q);
        
        const fetchedPlaybooks = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedPlaybooks.push({ id: doc.id, ...data });
        });
        
        setPlaybooks(fetchedPlaybooks);
      } catch (error) {
        console.error('Error fetching playbooks:', error);
      }
      setLoading(false);
    }

    fetchPlaybooks();
  }, [user]);

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  const handleViewPlaybook = (playbookId) => {
    navigate(`/playbooks/${playbookId}`);
  };

  const handleDeletePlaybook = async (playbookId) => {
    if (deleteConfirm === playbookId) {
      try {
        // Get the current user
        if (!user) {
          return;
        }

        // Get the playbook data
        const playbook = playbooks.find(p => p.id === playbookId);

        // Verify ownership
        if (!playbook) {
          return;
        }

        // Get the document reference and verify it exists
        const playbookRef = doc(db, 'playbooks', playbookId);
        const playbookDoc = await getDoc(playbookRef);
        
        if (!playbookDoc.exists()) {
          return;
        }

        const playbookData = playbookDoc.data();

        // Verify the user owns this playbook
        if (playbookData.user_id !== user.uid) {
          return;
        }

        // Delete the document
        await deleteDoc(playbookRef);
        setPlaybooks(playbooks.filter(p => p.id !== playbookId));
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error deleting playbook:', error);
          // Log additional error details in development only
          if (error.code) {
            console.error('Error code:', error.code);
          }
          if (error.message) {
            console.error('Error message:', error.message);
          }
        }
      }
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(playbookId);
    }
  };

  const handleRefreshPlaybook = async (playbookId) => {
    try {
      setRefreshingPlaybook(playbookId);
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        // Prompt user to sign in
        navigate('/login');
        return;
      }
      const idToken = await currentUser.getIdToken(true); // force refresh
      const playbook = playbooks.find(p => p.id === playbookId);
      if (!playbook) {
        return;
      }
      let portfolioToSend = playbook.portfolio;
      const response = await fetch(`${ODIKAI_API_BASE}/picks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          portfolio: portfolioToSend
        })
      });
      if (!response.ok) {
        throw new Error('Failed to refresh playbook');
      }
      const data = await response.json();
      // Update the playbook with new data
      const playbookRef = doc(db, 'playbooks', playbookId);
      await updateDoc(playbookRef, {
        last_updated: new Date().toISOString(),
        event_name: data.event_name,
        event_date: data.event_date,
        picks: data.picks.map(pick => savedPick(pick, data.event_name, data.event_date)),
      });
      setPlaybooks(playbooks.map(p => {
        if (p.id === playbookId) {
          return {
            ...p,
            last_updated: new Date().toISOString(),
            event_name: data.event_name,
            event_date: data.event_date,
            picks: data.picks.map(pick => savedPick(pick, data.event_name, data.event_date)),
          };
        }
        return p;
      }));
    } catch (error) {
      console.error('Error refreshing playbook:', error);
    } finally {
      setRefreshingPlaybook(null);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="page-container">
          <header className="page-header">
            <div className="header-content">
              <h1>My playbooks</h1>
              <p className="header-subtitle">Build, refresh and compare the playbooks you have made.</p>
            </div>
            <div className="header-stats">
              <div className="stat-card">
                <span className="stat-value">-</span>
                <span className="stat-label">Playbooks used</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">-</span>
                <span className="stat-label">Active events</span>
              </div>
            </div>
          </header>
          <div className="content-section loading-section">
            <div className="loading">
              <div className="loading-spinner"></div>
              <span>Loading your playbooks...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Saved playbooks keep their event as event_name
  const activeEvents = [...new Set(playbooks.map(p => p.event_name || p.event?.name).filter(Boolean))];
  // A custom playbook's portfolio_returns is the builder's yearly estimate as a plain fraction
  // (0.024 = +2.4%), not a multiplier. Playbooks without one are left out; none gives N/A.
  const withReturns = playbooks.map(p => p.portfolio_returns).filter(r => typeof r === 'number' && !Number.isNaN(r));
  const averageReturn = withReturns.length ? withReturns.reduce((sum, r) => sum + r, 0) / withReturns.length : null;

  return (
    <>

      <div className="page-wrapper">
        <div className="page-container">
          <header className="page-header">
            <div className="header-content">
              <h1>My playbooks</h1>
              <p className="header-subtitle">Build, refresh and compare the playbooks you have made.</p>
            </div>
            <div className="header-stats">
              <div className="stat-card">
                <span className="stat-value">{playbooks.length}/{userProfile?.settings?.available_playbooks || 5}</span>
                <span className="stat-label">Playbooks used</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{activeEvents.length}</span>
                <span className="stat-label">Active events</span>
              </div>
              <div className="stat-card">
                <span className={`stat-value ${toneOfSigned(averageReturn)}`}>
                  {formatExpectedReturn(averageReturn)}
                </span>
                <span className="stat-label">Avg expected return</span>
              </div>
            </div>
          </header>

          {playbooks.length > 0 && (
            <div className="create-button-wrapper">
              <div className="tooltip">
                <button 
                  className="create-button" 
                  onClick={() => navigate('/playbooks/build')}
                  disabled={playbooks.length >= (userProfile?.settings?.available_playbooks || 5)}
                >
                  <AddIcon /> Create playbook
                </button>
                {playbooks.length >= (userProfile?.settings?.available_playbooks || 5) && (
                  <span className="tooltip-text">
                    You have reached your maximum number of available playbooks
                  </span>
                )}
              </div>
            </div>
          )}

          {playbooks.length === 0 ? (
            <div className="empty-state">
              <MenuBookIcon sx={{ fontSize: 48, opacity: 0.6 }} />
              <p>You haven't created any playbooks yet. Start by creating your first playbook!</p>
              <button className="create-button" onClick={() => navigate('/playbooks/build')}>
                <AddIcon /> Create playbook
              </button>
            </div>
          ) : (
            <div className="playbooks-grid">
              {playbooks.map((playbook) => (
                <PlaybookCard
                  key={playbook.id}
                  playbook={playbook}
                  isActive={false}
                  onClick={() => {}}
                  onView={handleViewPlaybook}
                  onDelete={handleDeletePlaybook}
                  onRefresh={handleRefreshPlaybook}
                  deleteConfirm={deleteConfirm}
                  showActions={true}
                  isRefreshing={refreshingPlaybook === playbook.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
} 